import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/16/solid';
import { SidebarLayout } from '../Components/Catalyst/sidebar-layout';
import { memberViolationService } from '../clients/ApiClients';
import { ViolationReturn } from '../clients/rust-api-client';
import { apiErrorMessage, apiErrorStatus } from '../utils/apiErrors';
import { ErrorPage } from '../Components/Custom/ErrorPage';
import LoadingSpinner from '../LoadingSpinner';
import { Navbar } from '../Components/Catalyst/navbar';
import AppSidebar from '../AppSidebar';

import MemberViolationCommentThread from './comments/MemberViolationCommentThread';
import MemberAttachmentThumb from './comments/MemberAttachmentThumb';
import { MemberViolationProperties } from './linear/MemberViolationProperties';
import { StatusIcon } from './linear/StatusIcon';
import { positionIn, readListContext } from './linear/listContext';
import { shortRef } from './linear/violationMeta';

/**
 * A violation on the member's home, in the Linear-style detail layout
 * (#603 step 2): header with where-you-are-in-the-list stepping, the
 * message and the shared thread in the main column, read-only properties
 * beside it (below it on a phone).
 */
const ViolationDetailPage: React.FC = () => {
  const { violationId } = useParams<{
    violationId: string;
  }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [violation, setViolation] = useState<ViolationReturn | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const listContext = readListContext();
  const position = violationId ? positionIn(listContext, violationId) : null;
  const backTo = listContext?.from ?? '/violations';

  const fetchViolation = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError(null);
      // Ids are prefixed strings (`vio_…`); `Number(id)` was NaN.
      const response = await memberViolationService.getMyViolation({
        violationId: violationId as string,
      });
      setViolation(response.data);
    } catch (err) {
      console.error('Error fetching violation:', err);
      // Stepping with j/k from a violation that loaded must not leave
      // that one on screen under the next one's error.
      setViolation(null);
      // Someone else's violation answers 404, never 403 — the id space
      // is not probeable, so it reads as missing.
      setFetchError(
        apiErrorStatus(err) === 404
          ? "We couldn't find that violation. It may have been removed."
          : apiErrorMessage(err, "Couldn't load this violation.")
      );
    } finally {
      setLoading(false);
    }
  }, [violationId]);

  useEffect(() => {
    void fetchViolation();
  }, [fetchViolation]);

  // j / k step through the list, never while typing a reply.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) ||
          t.closest('[role="dialog"], [role="listbox"]'))
      )
        return;
      if (e.key === 'k' && position?.prevId) navigate(`/violation/${position.prevId}`);
      if (e.key === 'j' && position?.nextId) navigate(`/violation/${position.nextId}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [position?.prevId, position?.nextId, navigate]);

  if (loading) {
    return <LoadingSpinner />;
  }

  const stepButton =
    'rounded p-1 text-fg-muted hover:bg-fg/5 hover:text-fg disabled:opacity-30 disabled:hover:bg-transparent';

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="violations" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link
          to={backTo}
          className="text-fg-muted hover:text-fg"
        >
          Violations
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        {violation && (
          <>
            <StatusIcon status={violation.status} />
            <span className="hidden font-mono text-[12px] text-fg-muted sm:inline">
              {shortRef(violation.id)}
            </span>
            <span className="min-w-0 truncate font-medium text-fg">
              {violation.description}
            </span>
          </>
        )}
        {position && (
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <span
              className="mr-1 text-[12px] tabular-nums text-fg-muted"
              aria-label={`Violation ${position.index + 1} of ${position.total}`}
            >
              {position.index + 1} / {position.total}
            </span>
            <button
              type="button"
              onClick={() => position.prevId && navigate(`/violation/${position.prevId}`)}
              disabled={!position.prevId}
              title="Previous (k)"
              aria-label="Previous violation"
              className={stepButton}
            >
              <ChevronUpIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => position.nextId && navigate(`/violation/${position.nextId}`)}
              disabled={!position.nextId}
              title="Next (j)"
              aria-label="Next violation"
              className={stepButton}
            >
              <ChevronDownIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {!violation ? (
        <ErrorPage
          title="Couldn't load this violation"
          message={fetchError ?? "We couldn't find that violation."}
          onRetry={fetchError ? () => void fetchViolation() : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div className="min-w-0 space-y-8">
            <div>
              <h1 className="text-2xl/8 font-semibold tracking-tight text-fg">
                {violation.description}
              </h1>
              <p className="mt-3 whitespace-pre-line text-[15px]/6 text-fg-secondary">
                {violation.message}
              </p>
            </div>

            {((violation.attachments?.length ?? 0) > 0 ||
              violation.image_url ||
              violation.document_url) && (
              <section aria-labelledby="documentation-heading">
                <h2
                  id="documentation-heading"
                  className="mb-2 text-[13px] font-medium text-fg-muted"
                >
                  Photos &amp; documents
                </h2>
                {(violation.attachments?.length ?? 0) > 0 && (
                  <div className="mb-3 flex flex-wrap items-start gap-2">
                    {violation.attachments!.map((attachment) => (
                      <MemberAttachmentThumb key={attachment.id} attachment={attachment} />
                    ))}
                  </div>
                )}
                {/* Deprecated columns, deliberately still shown: for
                    violations written before attachments, these links are
                    the only pointer there is. */}
                <div className="space-y-1 text-[13px]">
                  {violation.image_url && (
                    <a
                      href={violation.image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-accent-fg hover:underline"
                    >
                      View image
                    </a>
                  )}
                  {violation.document_url && (
                    <a
                      href={violation.document_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-accent-fg hover:underline"
                    >
                      View document
                    </a>
                  )}
                </div>
              </section>
            )}

            <section aria-label="Activity">
              <MemberViolationCommentThread violationId={violationId as string} />
            </section>
          </div>

          <aside
            aria-label="Violation properties"
            className="lg:sticky lg:top-6 lg:self-start"
          >
            <MemberViolationProperties violation={violation} />
          </aside>
        </div>
      )}
    </SidebarLayout>
  );
};

export default ViolationDetailPage;
